import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import { createFormData } from "../../../utils/form-data/create-form-data"
import type {
  ProjectCreateData,
  ProjectLogHistoryResponse,
  ProjectPatchData,
  ProjectResponse,
  ProjectSubcontractorData,
  ProjectSubcontractorResponse,
  ProjectTypeContractDecisionData,
  ProjectTypeData,
  ProjectTypeMutationResponse,
  ProjectTypeRemoveData,
  ProjectTypeSaveAsTemplateData,
  ProjectTypeSaveAsTemplateResponse,
  ProjectTypeReorderData
} from "./services.types.ts"

const BASE_URL = `/project-management/projects`

const PATHS = {
  // Project
  createProject: `${BASE_URL}/`,
  getProject: (id: string) => `${BASE_URL}/${id}/details/`,
  getProjectLogHistory: (id: string) => `${BASE_URL}/${id}/log-history/`,
  addProjectType: (id: string) => `${BASE_URL}/${id}/add-project-type/`,
  removeProjectType: (id: string) => `${BASE_URL}/${id}/remove-project-type/`,
  saveProjectTypeAsTemplate: (id: string) =>
    `${BASE_URL}/${id}/save-project-type-as-template/`,
  updateProjectType: (id: string) => `${BASE_URL}/${id}/update-project-type/`,
  projectTypeContractDecision: (id: string) =>
    `${BASE_URL}/${id}/project-type-contract-decision/`,
  reorderProjectTypes: (id: string) =>
    `${BASE_URL}/${id}/reorder-project-types/`,
  //
  createAndAssignSubcontractor: (id: string) =>
    `${BASE_URL}/${id}/create-and-assign-subcontractor/`
}

export const createProject = async (data: ProjectCreateData) => {
  if (!data?.file_list?.length) {
    return request.api.post<
      BaseApiResponse<ProjectResponse>,
      BaseApiResponse<unknown>
    >(PATHS.createProject, data)
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormData(data, "file")

  return request.api.post<
    BaseApiResponse<ProjectResponse>,
    BaseApiResponse<unknown>
  >(PATHS.createProject, formData, { headers })
}

export const getProject = async (id: string) => {
  return request.api.get<ProjectResponse, BaseApiResponse<unknown>>(
    PATHS.getProject(id)
  )
}

export const patchProject = async (id: string, data: ProjectPatchData) => {
  const url = PATHS.getProject(id)
  const headers = { "Content-Type": "multipart/form-data" }

  if (data?.main_photo) {
    return request.api.post<
      BaseApiResponse<ProjectResponse>,
      BaseApiResponse<unknown>
    >(url, data, { headers })
  }

  if (!data?.file_list?.length) {
    return request.api.patch<
      BaseApiResponse<ProjectResponse>,
      BaseApiResponse<unknown>
    >(url, data)
  }

  const formData = createFormData(data, "file")
  return request.api.patch<
    BaseApiResponse<ProjectResponse>,
    BaseApiResponse<unknown>
  >(url, formData, { headers })
}

export const createAndAssignSubcontractor = async (
  id: string,
  data: ProjectSubcontractorData
) => {
  return request.api.post<
    BaseApiResponse<ProjectSubcontractorResponse>,
    BaseApiResponse<unknown>
  >(PATHS.createAndAssignSubcontractor(id), data)
}

export const addProjectTypeToProject = async (
  id: string,
  data: ProjectTypeData
) => {
  return request.api.post<
    BaseApiResponse<ProjectTypeMutationResponse>,
    BaseApiResponse<unknown>
  >(PATHS.addProjectType(id), data)
}

export const removeProjectTypeToProject = async (
  id: string,
  data: ProjectTypeRemoveData
) => {
  return request.api.post<
    BaseApiResponse<ProjectTypeMutationResponse>,
    BaseApiResponse<unknown>
  >(PATHS.removeProjectType(id), data)
}

export const saveProjectTypeAsTemplate = async (
  id: string,
  data: ProjectTypeSaveAsTemplateData
) => {
  return request.api.post<
    BaseApiResponse<ProjectTypeSaveAsTemplateResponse>,
    BaseApiResponse<unknown>
  >(PATHS.saveProjectTypeAsTemplate(id), data)
}

export const updateProjectTypeToProject = async (
  id: string,
  data: ProjectTypeData
) => {
  return request.api.post<
    BaseApiResponse<ProjectTypeMutationResponse>,
    BaseApiResponse<unknown>
  >(PATHS.updateProjectType(id), data)
}

export const reorderProjectTypes = async (
  id: string,
  data: ProjectTypeReorderData
) => {
  return request.api.post<
    BaseApiResponse<ProjectTypeMutationResponse>,
    BaseApiResponse<unknown>
  >(PATHS.reorderProjectTypes(id), data)
}

export const getProjectLogHistory = async (projectId: string) => {
  return request.api.get<ProjectLogHistoryResponse, BaseApiResponse<unknown>>(
    PATHS.getProjectLogHistory(projectId)
  )
}

export const recordProjectTypeContractDecision = async (
  id: string,
  data: ProjectTypeContractDecisionData
) => {
  return request.api.post<
    BaseApiResponse<ProjectTypeMutationResponse>,
    BaseApiResponse<unknown>
  >(PATHS.projectTypeContractDecision(id), data)
}
